import React from 'react';
import { useLocation } from 'react-router-dom';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { trackEnpalForYouEngagement, trackInstallAppClick } from '../services/enpalForYouService';
import { Button } from './Button';

/** Never cover SMS / magic-link login with the install overlay (iOS Web App can mis-detect standalone). */
function shouldSuppressInstallPromptForPath(pathname: string): boolean {
  if (pathname === '/login') {
    return true;
  }
  if (pathname.startsWith('/auth/')) {
    return true;
  }
  return false;
}

export const InstallPrompt: React.FC = () => {
  const location = useLocation();
  const { shouldShow, canUseNativePrompt, platform, promptInstall, dismiss } = useInstallPrompt();

  if (shouldSuppressInstallPromptForPath(location.pathname)) {
    return null;
  }

  if (!shouldShow) {
    return null;
  }

  const handleInstall = async () => {
    trackEnpalForYouEngagement({ type: 'pwa_install_cta_click', context: { label: 'Installa' } });
    trackInstallAppClick('Installa');
    if (canUseNativePrompt) {
      const outcome = await promptInstall();
      if (outcome != null) {
        trackEnpalForYouEngagement({
          type: 'pwa_install_native_prompt_result',
          context: { outcome },
        });
      }
    }
  };

  const handleDismiss = (buttonText: string, dismissKind: 'later' | 'understood') => {
    trackEnpalForYouEngagement({
      type: 'pwa_install_dismiss',
      context: { dismiss_kind: dismissKind, label: buttonText },
    });
    trackInstallAppClick(buttonText);
    dismiss();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.popup}>
        <img src="/enpal-logo.png" alt="Enpal" style={styles.logo} />
        <h2 style={styles.title}>Installa l'app di Enpal</h2>

        {canUseNativePrompt ? (
          <>
            <p style={styles.description}>
              Aggiungi l'app alla schermata Home per un accesso rapido
            </p>
            <div style={styles.buttons}>
              <Button onClick={handleInstall} fullWidth>
                Installa
              </Button>
              <button onClick={() => handleDismiss('Non ora', 'later')} style={styles.dismissButton}>
                Non ora
              </button>
            </div>
          </>
        ) : platform === 'ios' ? (
          <>
            <p style={styles.description}>
              Per installare l'app:
            </p>
            <div style={styles.instructions}>
              <p style={styles.step}>
                1. Tocca <span style={styles.icon}>⎙</span> in basso
              </p>
              <p style={styles.step}>
                2. Scorri e seleziona "Aggiungi a Home"
              </p>
            </div>
            <button onClick={() => handleDismiss('Ho capito', 'understood')} style={styles.dismissButton}>
              Ho capito
            </button>
          </>
        ) : (
          <>
            <p style={styles.description}>
              Per installare l'app, usa il menu del browser e seleziona "Installa" o "Aggiungi a Home"
            </p>
            <button onClick={() => handleDismiss('Ho capito', 'understood')} style={styles.dismissButton}>
              Ho capito
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  popup: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px 24px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
    maxWidth: '320px',
    width: '100%',
  },
  logo: {
    display: 'block',
    height: '28px',
    width: 'auto',
    margin: '0 auto 12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#000',
    textAlign: 'center',
    margin: '0 0 8px',
  },
  description: {
    fontSize: '13px',
    color: '#666',
    textAlign: 'center',
    margin: '0 0 16px',
  },
  instructions: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
  },
  step: {
    fontSize: '14px',
    color: '#333',
    margin: '0 0 8px',
  },
  icon: {
    fontSize: '16px',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  dismissButton: {
    background: 'none',
    border: 'none',
    color: '#666',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '6px',
    width: '100%',
  },
};
